import {useObserveSelector} from '@github-ui/use-observe-selector'
import {memo, type RefObject, useCallback, useSyncExternalStore} from 'react'
import {createPortal} from 'react-dom'

import {parseJsonObject} from '../../language-tools/json'
import type {JSONObject} from '../../language-tools/json/types'
import {keyAttr, propsAttr} from '../widgets/react-component'

interface ReactWidgetProps<P extends JSONObject> {
  target: Element
  widgetName: string
  Component: (props: P) => React.ReactNode
}

function parseProps<P extends JSONObject>(attrValue: string) {
  return parseJsonObject(attrValue) as P
}

/**
 * Component that hydrates a container with a React component and re-renders when the props attribute changes.
 */
function ReactWidget<P extends JSONObject>({target: container, widgetName, Component}: ReactWidgetProps<P>) {
  // Observe for changes to the props attribute to force a re-render if the widget updates. There's no need to observe
  // the key prop because it must remain constant for the life of the widget; if it changes the whole widget will remount.
  const subscribeToPropsChanges = useCallback(
    (onUpdate: () => void) => {
      const observer = new MutationObserver(() => onUpdate())
      observer.observe(container, {attributes: true, attributeFilter: [propsAttr(widgetName)]})
      return () => observer.disconnect()
    },
    [container, widgetName],
  )

  const propsJson = useSyncExternalStore(subscribeToPropsChanges, () => container.getAttribute(propsAttr(widgetName)))

  return propsJson !== null && createPortal(<Component {...parseProps<P>(propsJson)} />, container)
}

// memo doesn't play nice with generics so a cast is necessary
const MemoizedReactWidget = memo(ReactWidget) as typeof ReactWidget

/**
 * Enables us to render CodeMirror widgets with React components. CodeMirror handles rendering the containers, then
 * this hook queries for those containers and replaces the contents with React component instances using `Portal`s.
 *
 * To render widgets using React, create widget classes that extend `ReactComponentWidget` and render them using
 * `Decoration.widget` as usual. Then, call `useReactWidgets` in the editor component and render the result next to
 * the editor in the React component tree. Components will automatically re-render when the props change as CodeMirror
 * updates the view.
 *
 * @see ReactComponentWidget
 */
export function useReactWidgets<P extends JSONObject>(
  editorContainerRef: RefObject<ParentNode | null>,
  widgetName: string,
  Component: (props: P) => React.ReactNode,
) {
  const targets = useObserveSelector(`[${keyAttr(widgetName)}]`, undefined, editorContainerRef)

  return (
    <>
      {targets.map(target => {
        // The key is very important here; without it every component will be unmounted and remounted every time the
        // document changes
        const key = target.getAttribute(keyAttr(widgetName))
        if (!key) throw new Error(`Widget ${widgetName} is missing key attribute`)

        return <MemoizedReactWidget key={key} target={target} widgetName={widgetName} Component={Component} />
      })}
    </>
  )
}

try{ ReactWidget.displayName ||= 'ReactWidget' } catch {}
try{ MemoizedReactWidget.displayName ||= 'MemoizedReactWidget' } catch {}